/**
 * ファイルアップロードフック
 * ChatInput と ImageGenerator で共有
 */

import { useState, useCallback } from 'react';
import type { FileAttachment as FileAttachmentType, AttachedFileInfo } from '../types';
import { uploadFiles } from '../services/api';

export interface UseFileUploadOptions {
  sessionId: string;
  /** 画像ファイルのみをアップロードする */
  imageOnly?: boolean;
}

export interface UseFileUploadReturn {
  /** アップロード中かどうか */
  isUploading: boolean;
  /** ファイルをアップロードしてファイルIDを返す */
  uploadAttachedFiles: (files: FileAttachmentType[]) => Promise<string[]>;
  /** アップロードエラー */
  uploadError: string | null;
  /** エラーをクリア */
  clearUploadError: () => void;
}

export function useFileUpload(options: UseFileUploadOptions): UseFileUploadReturn {
  const { sessionId, imageOnly = false } = options;
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const clearUploadError = useCallback(() => {
    setUploadError(null);
  }, []);

  const uploadAttachedFiles = useCallback(
    async (files: FileAttachmentType[]): Promise<string[]> => {
      if (files.length === 0) return [];

      // 画像のみモードの場合はフィルタリング
      const filesToUpload = imageOnly
        ? files.filter((f) => f.mimeType.startsWith('image/'))
        : files;

      if (filesToUpload.length === 0) return [];

      setIsUploading(true);
      setUploadError(null);

      try {
        const uploadResponse = await uploadFiles({
          session_id: sessionId,
          files: filesToUpload,
        });

        // アップロード成功したファイルIDを取得
        const fileIds = uploadResponse.results
          .filter((result) => result.success && result.file_id)
          .map((result) => result.file_id!);

        return fileIds;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed';
        setUploadError(errorMessage);
        throw error;
      } finally {
        setIsUploading(false);
      }
    },
    [sessionId, imageOnly]
  );

  return {
    isUploading,
    uploadAttachedFiles,
    uploadError,
    clearUploadError,
  };
}

/**
 * 添付ファイル情報を作成（表示用）
 */
export function createAttachedFileInfos(files: FileAttachmentType[]): AttachedFileInfo[] {
  return files.map((file, index) => ({
    file_id: `temp-${index}`, // 一時ID（アップロード後に更新される）
    filename: file.filename,
    mimeType: file.mimeType,
  }));
}
